import { providers } from '../../providers';
import { tencent } from '../tencent';
import { wavespeed } from '../wavespeed';
import { getUiOptionsForModel } from '../ui-options';
import type { VideoModelEntry } from '../types';
import {
    rmbCost480p, usdCost480p, rmbCost720p, usdCost720p, rmbCost1080p, usdCost1080p, rmbCost4k, usdCost4k,
    rmbRefCost480p, usdRefCost480p, rmbRefCost720p, usdRefCost720p, rmbRefCost1080p, usdRefCost1080p, rmbRefCost4k, usdRefCost4k,
    rmbCost480pFast, usdCost480pFast, rmbCost720pFast, usdCost720pFast,
    rmbRefCost480pFast, usdRefCost480pFast, rmbRefCost720pFast, usdRefCost720pFast,
    rmbCost480pMini, usdCost480pMini, rmbCost720pMini, usdCost720pMini,
    rmbRefCost480pMini, usdRefCost480pMini, rmbRefCost720pMini, usdRefCost720pMini,
} from './seedance-pricing';

// 系数
const COST_FACTOR = 1.6;

// Seedance 2.5 国际系数
const SEEDANCE_2_5_COST_FACTOR = 1.5 * 1.5;

export const volcengineInternationalModels: VideoModelEntry[] = [
    {
        id: 'seedance-2.5-uncensored',
        chef: providers.bytedance,
        frameModeLabels: { ref: 'Omni Reference' },
        providers: [
            {
                ...providers.bytedance,
                model: tencent('seedance-2.5'),
                // 积分: 美元
                getCost: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = duration + inputDur;
                    if (inputDur > 0) {
                        if (quality === '480p') return totalDur * usdRefCost480p * SEEDANCE_2_5_COST_FACTOR;
                        if (quality === '720p') return totalDur * usdRefCost720p * SEEDANCE_2_5_COST_FACTOR;
                        if (quality === '1080p') return totalDur * usdRefCost1080p * SEEDANCE_2_5_COST_FACTOR;
                        return totalDur * usdRefCost1080p * SEEDANCE_2_5_COST_FACTOR;
                    } else {
                        if (quality === '480p') return totalDur * usdCost480p * SEEDANCE_2_5_COST_FACTOR;
                        if (quality === '720p') return totalDur * usdCost720p * SEEDANCE_2_5_COST_FACTOR;
                        if (quality === '1080p') return totalDur * usdCost1080p * SEEDANCE_2_5_COST_FACTOR;
                        return totalDur * usdCost1080p * SEEDANCE_2_5_COST_FACTOR;
                    }
                },
                // 积分: 人民币
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = duration + inputDur;
                    if (inputDur > 0) {
                        if (quality === '480p') return totalDur * rmbRefCost480p * SEEDANCE_2_5_COST_FACTOR;
                        if (quality === '720p') return totalDur * rmbRefCost720p * SEEDANCE_2_5_COST_FACTOR;
                        if (quality === '1080p') return totalDur * rmbRefCost1080p * SEEDANCE_2_5_COST_FACTOR;
                        return totalDur * rmbRefCost1080p * SEEDANCE_2_5_COST_FACTOR;
                    } else {
                        if (quality === '480p') return totalDur * rmbCost480p * SEEDANCE_2_5_COST_FACTOR;
                        if (quality === '720p') return totalDur * rmbCost720p * SEEDANCE_2_5_COST_FACTOR;
                        if (quality === '1080p') return totalDur * rmbCost1080p * SEEDANCE_2_5_COST_FACTOR;
                        return totalDur * rmbCost1080p * SEEDANCE_2_5_COST_FACTOR;
                    }
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: [],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['match', '9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 30, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: true, maxCount: 10, maxSize: '200MB', minDuration: '2s', maxDuration: '30s', minPixels: 409600, maxPixels: 8295044, formats: ['video/mp4', 'video/quicktime'] },
                    audio: { allow: true, maxCount: 10, maxSize: '15MB', minDuration: '2s', maxDuration: '30s', formats: ['audio/mpeg', 'audio/wav'] },
                    mediaDurationLimit: { enabled: true, maxTotalDuration: '30s' },
                    maxMediaCount: 20,
                    maxTotalCount: 50,
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video|audio'],
        },
        supportsEdit: true,
        supportsInput: ['t2v','first','first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('seedance-2.5'),
    },
    {
        id: 'seedance-2.5-v2v-uncensored',
        chef: providers.bytedance,
        frameModeLabels: { ref: 'Omni Reference' },
        providers: [
            {
                ...providers.bytedance,
                model: tencent('seedance-2.5-v2v'),
                getCost: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = inputDur > 0 ? inputDur : duration;
                    if (quality === '480p') return totalDur * usdRefCost480p * SEEDANCE_2_5_COST_FACTOR;
                    if (quality === '720p') return totalDur * usdRefCost720p * SEEDANCE_2_5_COST_FACTOR;
                    if (quality === '1080p') return totalDur * usdRefCost1080p * SEEDANCE_2_5_COST_FACTOR;
                    return totalDur * usdRefCost1080p * SEEDANCE_2_5_COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = inputDur > 0 ? inputDur : duration;
                    if (quality === '480p') return totalDur * rmbRefCost480p * SEEDANCE_2_5_COST_FACTOR;
                    if (quality === '720p') return totalDur * rmbRefCost720p * SEEDANCE_2_5_COST_FACTOR;
                    if (quality === '1080p') return totalDur * rmbRefCost1080p * SEEDANCE_2_5_COST_FACTOR;
                    return totalDur * rmbRefCost1080p * SEEDANCE_2_5_COST_FACTOR;
                },
            },
        ],
        configByMode: {
            v2v: {
                aspectRatios: [],
                durations: [],
                qualities: ['480p', '720p', '1080p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 30, maxSize: '30MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: true, maxCount: 1, maxSize: '200MB', minDuration: '4s', maxDuration: '30s', minPixels: 409600, maxPixels: 8295044, formats: ['video/mp4', 'video/quicktime'] },
                    audio: { allow: true, maxCount: 10, maxSize: '15MB', minDuration: '2s', maxDuration: '30s', formats: ['audio/mpeg', 'audio/wav'] },
                    maxMediaCount: 11,
                    maxTotalCount: 41,
                },
            },
        },
        precheckByMode: {
            v2v: ['prompt', 'video'],
        },
        supportsEdit: true,
        supportsInput: ['v2v'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('seedance-2.5'),
    },
    {
        id: 'seedance-2.0-uncensored',
        chef: providers.bytedance,
        frameModeLabels: { ref: 'Omni Reference' },
        providers: [
            {
                ...providers.bytedance,
                model: tencent('seedance-2.0'),
                modelBackup: [wavespeed('seedance-2.0-uncensored')],
                // 积分: 美元
                getCost: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = duration + inputDur;
                    if (inputDur > 0) {
                        if (quality === '480p') return totalDur * usdRefCost480p * COST_FACTOR;
                        if (quality === '720p') return totalDur * usdRefCost720p * COST_FACTOR;
                        if (quality === '1080p') return totalDur * usdRefCost1080p * COST_FACTOR;
                        if (quality === '4k') return totalDur * usdRefCost4k * COST_FACTOR;
                        return totalDur * usdRefCost4k * COST_FACTOR;
                    } else {
                        if (quality === '480p') return totalDur * usdCost480p * COST_FACTOR;
                        if (quality === '720p') return totalDur * usdCost720p * COST_FACTOR;
                        if (quality === '1080p') return totalDur * usdCost1080p * COST_FACTOR;
                        if (quality === '4k') return totalDur * usdCost4k * COST_FACTOR;
                        return totalDur * usdCost4k * COST_FACTOR;
                    }
                },
                // 积分: 人民币
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = duration + inputDur;
                    if (inputDur > 0) {
                        if (quality === '480p') return totalDur * rmbRefCost480p * COST_FACTOR;
                        if (quality === '720p') return totalDur * rmbRefCost720p * COST_FACTOR;
                        if (quality === '1080p') return totalDur * rmbRefCost1080p * COST_FACTOR;
                        if (quality === '4k') return totalDur * rmbRefCost4k * COST_FACTOR;
                        return totalDur * rmbRefCost4k * COST_FACTOR;
                    } else {
                        if (quality === '480p') return totalDur * rmbCost480p * COST_FACTOR;
                        if (quality === '720p') return totalDur * rmbCost720p * COST_FACTOR;
                        if (quality === '1080p') return totalDur * rmbCost1080p * COST_FACTOR;
                        if (quality === '4k') return totalDur * rmbCost4k * COST_FACTOR;
                        return totalDur * rmbCost4k * COST_FACTOR;
                    }
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: ['match', '9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p', '1080p', '4k'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p', '1080p', '4k'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 9, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: true, maxCount: 3, maxSize: '50MB', minDuration: '2s', maxDuration: '15.1s', minPixels: 409600, maxPixels: 927408, formats: ['video/mp4','video/mov'] },
                    audio: { allow: true, maxCount: 3, maxSize: '15MB', minDuration: '2s', maxDuration: '15.1s', formats: ['audio/mpeg'] },
                    mediaDurationLimit: { enabled: true, maxTotalDuration: '15.1s' },
                    maxMediaCount: 3,
                    maxTotalCount: 12,
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video'],
        },
        supportsEdit: true,
        supportsInput: ['t2v','first','first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('seedance-2.0-uncensored'),
    },
    {
        id: 'seedance-2.0-fast-uncensored',
        chef: providers.bytedance,
        frameModeLabels: { ref: 'Omni Reference' },
        providers: [
            {
                ...providers.bytedance,
                model: tencent('seedance-2.0-fast'),
                // 积分: 美元
                getCost: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = duration + inputDur;
                    if (inputDur > 0) {
                        if (quality === '480p') return totalDur * usdRefCost480pFast * COST_FACTOR;
                        if (quality === '720p') return totalDur * usdRefCost720pFast * COST_FACTOR;
                        return totalDur * usdRefCost720pFast * COST_FACTOR;
                    } else {
                        if (quality === '480p') return totalDur * usdCost480pFast * COST_FACTOR;
                        if (quality === '720p') return totalDur * usdCost720pFast * COST_FACTOR;
                        return totalDur * usdCost720pFast * COST_FACTOR;
                    }
                },
                // 积分: 人民币
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = duration + inputDur;
                    if (inputDur > 0) {
                        if (quality === '480p') return totalDur * rmbRefCost480pFast * COST_FACTOR;
                        if (quality === '720p') return totalDur * rmbRefCost720pFast * COST_FACTOR;
                        return totalDur * rmbRefCost720pFast * COST_FACTOR;
                    } else {
                        if (quality === '480p') return totalDur * rmbCost480pFast * COST_FACTOR;
                        if (quality === '720p') return totalDur * rmbCost720pFast * COST_FACTOR;
                        return totalDur * rmbCost720pFast * COST_FACTOR;
                    }
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: ['match', '9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 9, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: true, maxCount: 3, maxSize: '50MB', minDuration: '2s', maxDuration: '15.1s', minPixels: 409600, maxPixels: 927408, formats: ['video/mp4','video/mov'] },
                    audio: { allow: true, maxCount: 3, maxSize: '15MB', minDuration: '2s', maxDuration: '15.1s', formats: ['audio/mpeg'] },
                    mediaDurationLimit: { enabled: true, maxTotalDuration: '15.1s' },
                    maxMediaCount: 3,
                    maxTotalCount: 12,
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video'],
        },
        supportsEdit: true,
        supportsInput: ['t2v','first','first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('seedance-2.0-fast-uncensored'),
    },
    {
        id: 'seedance-2.0-mini-uncensored',
        // 国际版默认模型（优先级高于 grok 的 default: true；仅 international 标签用户可见，
        // 其他用户在过滤后的模型表中取不到，自然回退到 grok）。
        default: 2,
        chef: providers.bytedance,
        frameModeLabels: { ref: 'Omni Reference' },
        providers: [
            {
                ...providers.bytedance,
                model: tencent('seedance-2.0-mini'),
                // 积分: 美元
                getCost: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = duration + inputDur;
                    if (inputDur > 0) {
                        if (quality === '480p') return totalDur * usdRefCost480pMini * COST_FACTOR;
                        if (quality === '720p') return totalDur * usdRefCost720pMini * COST_FACTOR;
                        return totalDur * usdRefCost720pMini * COST_FACTOR;
                    } else {
                        if (quality === '480p') return totalDur * usdCost480pMini * COST_FACTOR;
                        if (quality === '720p') return totalDur * usdCost720pMini * COST_FACTOR;
                        return totalDur * usdCost720pMini * COST_FACTOR;
                    }
                },
                // 积分: 人民币
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const inputDur = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const totalDur = duration + inputDur;
                    if (inputDur > 0) {
                        if (quality === '480p') return totalDur * rmbRefCost480pMini * COST_FACTOR;
                        if (quality === '720p') return totalDur * rmbRefCost720pMini * COST_FACTOR;
                        return totalDur * rmbRefCost720pMini * COST_FACTOR;
                    } else {
                        if (quality === '480p') return totalDur * rmbCost480pMini * COST_FACTOR;
                        if (quality === '720p') return totalDur * rmbCost720pMini * COST_FACTOR;
                        return totalDur * rmbCost720pMini * COST_FACTOR;
                    }
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: ['match', '9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '720p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 9, maxSize: '5MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: true, maxCount: 3, maxSize: '50MB', minDuration: '2s', maxDuration: '15.1s', minPixels: 409600, maxPixels: 927408, formats: ['video/mp4','video/mov'] },
                    audio: { allow: true, maxCount: 3, maxSize: '15MB', minDuration: '2s', maxDuration: '15.1s', formats: ['audio/mpeg'] },
                    mediaDurationLimit: { enabled: true, maxTotalDuration: '15.1s' },
                    maxMediaCount: 3,
                    maxTotalCount: 12,
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video'],
        },
        supportsEdit: true,
        supportsInput: ['t2v','first','first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('seedance-2.0-mini-uncensored'),
    },
];